import {
  ArrowBack as ArrowBackIcon,
  Refresh as RefreshIcon,
} from "@mui/icons-material";
import { Box, IconButton, Typography } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { prescriptionApi } from "apis";
import { CustomBreadcrumbs } from "components/common/CustomBreadcrumbs";
import CustomTabs from "components/common/CustomTabs";
import DashboardLayout from "components/layouts/DashboardLayout";
import { useToast } from "contexts/ToastContext";
import { useGetMedicinesInPrescription } from "hooks/useMedicine";
import {
  PrescriptionOrderSummaryDto,
  PrescriptionOrderSummaryDtoExternalResourceEnum,
  PrescriptionResponseDtoStatusEnum,
} from "libs/cellera-sdk";
import React, { useEffect, useMemo } from "react";
import { useNavigate, useParams } from "react-router";
import { useCelleraTheme } from "theme/useCelleraTheme";
import { MedicineWithOrderDetails } from "types/medicine";

import { ActionButton } from "./components/PatientDetailPageContainer";
import {
  ActivityLog,
  ActivityLogEntry,
} from "./components/Prescription/ActivityLog";
import { FeesAndTotalSummary } from "./components/Prescription/FeesAndTotalSummary";
import Formula from "./components/Prescription/Formula/Formula";
import EmptyMedicine from "./components/Prescription/Medicine/EmptyMedicine";
import LoadingMedicine from "./components/Prescription/Medicine/LoadingMedicine";
import MedicineTable from "./components/Prescription/Medicine/MedicineTable";
import { PrescriptionHeader } from "./components/Prescription/PrescriptionHeader";
import { IPrescriptionOrderInformationCardProps } from "./components/Prescription/PrescriptionOtherInformationCard";
import { IPrescriptionPatientCardProps } from "./components/Prescription/PrescriptionPatientCard";
import { IPrescriptionPrescriberCardProps } from "./components/Prescription/PrescriptionPrescriberCard";

export const PrescriptionDetailPage: React.FC = () => {
  const { prescriptionId } = useParams();
  const { colors } = useCelleraTheme();
  const { showError } = useToast();
  const {
    data: prescription,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["prescription", prescriptionId],
    queryFn: () =>
      prescriptionApi.providerGetPrescriptionById({
        prescriptionId: prescriptionId as string,
      }),
  });

  const totalFormulaPrice = useMemo(() => {
    return (
      prescription?.orders
        ?.filter(
          (order: PrescriptionOrderSummaryDto) =>
            order.externalResource ===
            PrescriptionOrderSummaryDtoExternalResourceEnum.PersonalizedNutrients
        )
        .reduce(
          (sum: number, order: PrescriptionOrderSummaryDto) =>
            sum + (order.total ?? 0),
          0
        ) || 0
    );
  }, [prescription?.orders]);

  const navigate = useNavigate();

  useEffect(() => {
    if (error) {
      showError("Error", error.message);
    }
  }, [error, showError]);

  const { data: medicinesInPrescriptionData, isLoading: isLoadingMedicines } =
    useGetMedicinesInPrescription(prescriptionId);

  const medicinesWithDetails: MedicineWithOrderDetails[] = useMemo(() => {
    if (!medicinesInPrescriptionData?.medicines) {
      return [];
    }
    return medicinesInPrescriptionData.medicines.map((item) => ({
      medicine: item.medicine,
      orderDetails: {
        medicineId: item.medicine.id,
        sig: item.sig,
        dispense: item.dispense,
        refill: item.refill,
      },
    }));
  }, [medicinesInPrescriptionData]);

  // Mock activity log data
  const activityLogEntries: ActivityLogEntry[] = useMemo(() => {
    const patientDisplay =
      [prescription?.patient?.firstName, prescription?.patient?.lastName]
        .filter(Boolean)
        .join(" ") ?? prescription?.patient?.email;
    const entries = [
      {
        timestamp: prescription?.createdAt?.toISOString() ?? "",
        description: `Order created by ${prescription?.provider?.firstName} ${prescription?.provider?.lastName}`,
      },
      {
        timestamp: prescription?.createdAt?.toISOString() ?? "",
        description: `Prescription sent to ${patientDisplay}`,
      },
    ];
    if (prescription?.status === PrescriptionResponseDtoStatusEnum.Paid) {
      entries.push({
        timestamp: prescription?.updatedAt?.toISOString() ?? "",
        description: `Payment completed by ${patientDisplay}`,
      });
      entries.push({
        timestamp: prescription?.updatedAt?.toISOString() ?? "",
        description: `Prescription marked as PAID`,
      });
    }
    if (prescription?.status === PrescriptionResponseDtoStatusEnum.Cancelled) {
      const cancelBy =
        [prescription?.cancelBy?.firstName, prescription?.cancelBy?.lastName]
          .filter(Boolean)
          .join(" ") ?? prescription?.cancelBy?.email;
      if (cancelBy) {
        entries.push({
          timestamp: prescription?.updatedAt?.toISOString() ?? "",
          description: `Prescription cancelled by ${cancelBy}`,
        });
      }
      entries.push({
        timestamp: prescription?.updatedAt?.toISOString() ?? "",
        description: `Prescription marked as CANCELLED`,
      });
    }
    return entries;
  }, [prescription]);

  const handleViewInvoice = () => {
    console.log("View invoice clicked");
  };

  const allActionButtons: ActionButton[] = useMemo(
    () => [
      {
        icon: <ArrowBackIcon />,
        onClick: () => navigate("/prescription-management"),
        tooltip: "Go back",
      },
      {
        icon: <RefreshIcon />,
        onClick: () => {},
        tooltip: "Refresh",
      },
    ],
    [navigate]
  );
  const breadcrumbs = useMemo(
    () => [
      { label: "Dashboard", href: "/dashboard", order: 0 },
      {
        label: "Prescription Management",
        href: "/prescription-management",
        order: 1,
      },
    ],
    []
  );
  const orderInformation = useMemo(
    (): IPrescriptionOrderInformationCardProps => ({
      orderId: prescription?.id?.toString() ?? "",
      status: prescription?.status as
        | PrescriptionResponseDtoStatusEnum
        | undefined,
      createdAt: prescription?.createdAt,
      paidAt:
        prescription?.status === PrescriptionResponseDtoStatusEnum.Paid
          ? prescription?.updatedAt
          : undefined,
      cancelledAt:
        prescription?.status === PrescriptionResponseDtoStatusEnum.Cancelled
          ? prescription?.updatedAt
          : undefined,
    }),
    [
      prescription?.createdAt,
      prescription?.id,
      prescription?.status,
      prescription?.updatedAt,
    ]
  );

  const patientInformation = useMemo(
    (): IPrescriptionPatientCardProps => ({
      name:
        [
          prescription?.patient?.firstName,
          prescription?.patient?.middleName,
          prescription?.patient?.lastName,
        ]
          .filter(Boolean)
          .join(" ") ?? "N/A",
      email: prescription?.patient?.email ?? "N/A",
      phone: prescription?.patient?.phone ?? "N/A",
      patientId: prescription?.patient?.patientId ?? "",
    }),
    [prescription?.patient]
  );

  const prescriberInformation = useMemo(
    (): IPrescriptionPrescriberCardProps => ({
      name:
        [prescription?.provider?.firstName, prescription?.provider?.lastName]
          .filter(Boolean)
          .join(" ") ?? "N/A",
      email: prescription?.provider?.email ?? "N/A",
      phone: prescription?.provider?.phoneNumber ?? "N/A",
    }),
    [prescription?.provider]
  );
  if (isLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "300px",
        }}
      >
        <Typography variant="body1" sx={{ color: colors.text.secondary }}>
          Loading...
        </Typography>
      </Box>
    );
  }
  return (
    <DashboardLayout title="Prescription Details">
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <CustomBreadcrumbs
          items={breadcrumbs}
          lastItem={"Prescription Details"}
        />
        {allActionButtons.length > 0 && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            {allActionButtons.map((button, index) => (
              <IconButton
                key={index}
                onClick={button.onClick}
                disabled={button.disabled}
                sx={{ color: "text.secondary", ...button.sx }}
                size="small"
                title={button.tooltip}
              >
                {button.icon}
              </IconButton>
            ))}
          </Box>
        )}
      </Box>

      <PrescriptionHeader
        orderInformation={orderInformation}
        patientInformation={patientInformation}
        prescriberInformation={prescriberInformation}
      />

      <Box sx={{ mt: 3 }}>
        <CustomTabs
          tabs={[
            {
              label: "Medicine",
              content: (
                <Box
                  sx={{
                    border: `1px solid ${colors.border.main}`,
                    borderRadius: 1,
                    overflow: "hidden",
                    maxHeight: "550px",
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  <MedicineTable
                    medicines={medicinesWithDetails}
                    loadingState={
                      isLoadingMedicines ? <LoadingMedicine /> : undefined
                    }
                    emptyState={
                      medicinesWithDetails.length === 0 &&
                      !isLoadingMedicines ? (
                        <EmptyMedicine
                          onCreateMedicine={() => {}}
                          readOnly={true}
                        />
                      ) : undefined
                    }
                    readOnly={true}
                    cellPadding={1}
                    sigMaxWidth={300}
                    actionsCellWidth="120px"
                  />
                </Box>
              ),
            },
            {
              label: "Supplement",
              content: (
                <Formula
                  prescriptionId={prescriptionId}
                  patientId={prescription?.patient?.patientId ?? ""}
                  readOnly={true}
                />
              ),
            },
          ]}
          defaultTab={0}
        />
      </Box>

      {prescription?.status === PrescriptionResponseDtoStatusEnum.Paid && (
        <Box>
          <FeesAndTotalSummary
            formulaTotal={totalFormulaPrice}
            status={prescription?.status as PrescriptionResponseDtoStatusEnum}
            onViewInvoice={handleViewInvoice}
          />
        </Box>
      )}
      <Box sx={{ mt: 3 }}>
        <ActivityLog activities={activityLogEntries} />
      </Box>
    </DashboardLayout>
  );
};
